import React from 'react';
import { CloseCircleFilled, ReloadOutlined, WarningOutlined, CloudUploadOutlined } from '@ant-design/icons';
import { Image, Tooltip, theme } from 'antd';
import type { UploadItem } from '../../domain/entities/UploadItem.js';
import { UploadProgressBar } from './UploadProgressBar.js';

type TileKind = 'image' | 'video' | 'audio' | 'file';

interface Props {
  item: UploadItem;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
  size?: number;
  /** Optional explicit tile width; defaults to `size` (square), matching prior behavior. */
  width?: number;
  /** Optional explicit preview kind. Omit to keep the original image-or-icon sniffing. */
  kind?: TileKind;
}

export function UploadItemTile({ item, onRemove, onRetry, size = 100, width, kind }: Props) {
  const { token } = theme.useToken();
  const tileWidth = width ?? size;
  const previewSrc = item.objectUrl ?? item.publicUrl ?? undefined;
  const isVideo = item.file.type.startsWith('video/') || /\.(mp4|mov|webm|m4v|mpeg|avi|mkv)(\?.*)?$/i.test(previewSrc ?? item.file.name ?? '');
  const isAudio = item.file.type.startsWith('audio/') || /\.(mp3|wav|ogg|aac|m4a|weba)(\?.*)?$/i.test(previewSrc ?? item.file.name ?? '');
  const isImage = item.file.type.startsWith('image/') || (!isVideo && !isAudio && !!previewSrc && /\.(jpg|jpeg|png|gif|webp|svg|avif|bmp)(\?.*)?$/i.test(previewSrc ?? item.file.name ?? ''));
  const resolvedKind: TileKind = kind ?? (isVideo ? 'video' : isAudio ? 'audio' : isImage ? 'image' : (previewSrc ? 'image' : 'file'));


  return (
    <div style={{ position: 'relative', width: tileWidth, height: size, flexShrink: 0 }}>
      {/* Preview */}
      <div style={{ width: tileWidth, height: size, borderRadius: 8, overflow: 'hidden', background: token.colorFillSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {resolvedKind === 'image' && previewSrc ? (
          <Image
            src={previewSrc}
            alt=""
            width="100%"
            height="100%"
            style={{ objectFit: 'cover', display: 'block' }}
            preview={{ mask: null }}
          />
        ) : resolvedKind === 'video' && previewSrc ? (
          <video src={previewSrc} controls style={{ width: '100%', height: '100%', background: '#000' }} />
        ) : resolvedKind === 'audio' && previewSrc ? (
          <audio src={previewSrc} controls style={{ width: '100%', padding: '0 4px' }} />
        ) : resolvedKind === 'file' && previewSrc ? (
          <a
            href={previewSrc}
            target="_blank"
            rel="noreferrer"
            style={{ padding: '0 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}
          >
            {item.file.name}
          </a>
        ) : (
          <CloudUploadOutlined style={{ fontSize: 28, color: token.colorTextQuaternary }} />
        )}
      </div>

      {/* Progress overlay */}
      {item.status === 'uploading' && (
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, borderRadius: '0 0 8px 8px', padding: '0 2px 2px' }}>
          <UploadProgressBar progress={item.progress} />
        </div>
      )}

      {/* Remove / retry button */}
      {(item.status === 'done' || item.status === 'failed') && (
        <Tooltip title={item.status === 'failed' ? 'Thử lại' : 'Xoá'}>
          <div
            onClick={() => item.status === 'failed' ? onRetry(item.id) : onRemove(item.id)}
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              cursor: 'pointer',
              background: item.status === 'failed' ? '#ff7a00' : 'rgba(0,0,0,0.5)',
              borderRadius: '50%',
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {item.status === 'failed'
              ? <ReloadOutlined style={{ fontSize: 11, color: '#fff' }} />
              : <CloseCircleFilled style={{ fontSize: 12, color: '#fff' }} />}
          </div>
        </Tooltip>
      )}

      {/* Failed icon */}
      {item.status === 'failed' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <WarningOutlined style={{ fontSize: 28, color: token.colorError }} />
        </div>
      )}

      {/* Offline queued badge */}
      {item.status === 'offlineQueued' && (
        <div style={{ position: 'absolute', bottom: 4, left: 0, right: 0, textAlign: 'center' }}>
          <CloudUploadOutlined style={{ fontSize: 16, color: token.colorWarning }} />
        </div>
      )}
    </div>
  );
}
